import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  const load = useCallback(() => {
    api.listNotifications().then(setItems).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.markNotificationRead(n.key); } catch { /* best-effort */ }
      setItems((arr) => arr.map((x) => (x.key === n.key ? { ...x, read: true } : x)));
    }
    if (n.link) nav(n.link);
  };

  // Follow-up nudges carry a doc_id (older ones: parse it out of the key,
  // format quote-followup-{uuid}-{YYYY-MM-DD})
  const followupDocId = (n) => {
    if (!String(n.key || "").startsWith("quote-followup-")) return null;
    if (n.doc_id) return n.doc_id;
    return n.key.slice("quote-followup-".length, -11);
  };

  const snooze = async (n, body) => {
    const docId = followupDocId(n);
    if (!docId) return;
    try {
      await api.snoozeFollowup(docId, body);
      setItems((arr) => arr.map((x) => (x.key === n.key ? { ...x, read: true } : x)));
      load();
    } catch { /* best-effort */ }
  };

  return (
    <div ref={ref} className="fixed top-3 right-4 z-40" data-testid="notification-bell">
      <button
        data-testid="bell-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative bg-white border border-slate-200 shadow-sm rounded-full p-2.5 hover:border-[#0e6939] transition-colors"
        title="Notifications"
      >
        <Bell size={16} className="text-slate-600" />
        {unread > 0 && (
          <span
            data-testid="bell-unread-count"
            className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="bell-dropdown"
          className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden"
        >
          <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">
            Notifications
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {items.length === 0 ? (
              <p className="text-sm muted p-4">Nothing yet — you're all caught up.</p>
            ) : (
              items.slice(0, 15).map((n) => (
                <div key={n.key} className={`${n.read ? "opacity-60" : ""}`}>
                  <button
                    data-testid={`notif-${n.key}`}
                    onClick={() => openItem(n)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#0e6939] shrink-0" />}
                      <span className="truncate">{n.title}</span>
                    </div>
                    <div className="text-xs muted mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] muted mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </button>
                  {followupDocId(n) && !n.read && (
                    <div className="flex items-center gap-1.5 px-4 pb-2.5 -mt-1" data-testid={`snooze-row-${n.key}`}>
                      <span className="text-[10px] muted">Snooze:</span>
                      <button
                        data-testid={`snooze-3d-${n.key}`}
                        onClick={() => snooze(n, { days: 3 })}
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 hover:border-[#0e6939] hover:text-[#0e6939]"
                      >
                        3 days
                      </button>
                      <button
                        data-testid={`snooze-7d-${n.key}`}
                        onClick={() => snooze(n, { days: 7 })}
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 hover:border-[#0e6939] hover:text-[#0e6939]"
                      >
                        7 days
                      </button>
                      <button
                        data-testid={`snooze-forever-${n.key}`}
                        onClick={() => snooze(n, { forever: true })}
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 hover:border-red-400 hover:text-red-600"
                      >
                        Don't remind again
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
